import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { AppCard } from '../ui/AppCard';
import { Button } from '../ui/Button';
import { TextField } from '../ui/TextField';
import type { Mapping } from '../../types';

// Providers whose Source Ask Zmist builds. Their spec is not a list of
// field -> signal rules (the visual mapping editor's model): it names a channel,
// a schema, what to read and how often, and the provider re-reads it itself.
// This card edits every one of those fields in place, so such a Source is no
// longer reachable only through the raw-JSON editor.
export const ASK_PROVIDERS = ['web-auto', 'github-api', 'sec-xbrl'];

type Obj = Record<string, unknown>;
const obj = (v: unknown): Obj => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : {});
const str = (v: unknown) => (typeof v === 'string' ? v : '');
const list = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => String(x)) : []);
const csv = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

const INTERVALS: [number, string][] = [
  [3600, 'Every hour'],
  [21600, 'Every 6 hours'],
  [43200, 'Every 12 hours'],
  [86400, 'Daily'],
  [604800, 'Weekly'],
];
const FORMATS = ['xlsx', 'docx', 'ods', 'json', 'csv', 'tsv', 'xml', 'yaml', 'html', 'prometheus'];
const MODES = ['table', 'cross', 'snapshot', 'text'];

const selectCls = 'w-full rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-3 py-2.5 text-[15px]';
const areaCls = `${selectCls} min-h-[72px] resize-y`;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5 w-full">
      <span className="text-sm font-medium text-black/70 dark:text-white/70">{label}</span>
      {children}
    </label>
  );
}

// What was detected in one document - how it is read on every poll. Editing it
// changes how the next poll reads the file; a wrong value fails that poll with a
// reason instead of storing a guess.
function PlanFields({ plan, onChange }: { plan: Obj; onChange: (p: Obj) => void }) {
  const set = (k: string, v: unknown) => {
    const next = { ...plan };
    if (v === '' || (Array.isArray(v) && v.length === 0)) delete next[k];
    else next[k] = v;
    onChange(next);
  };
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="File type">
        <select aria-label="File type" className={selectCls} value={str(plan.format)} onChange={(e) => set('format', e.target.value)}>
          {[...new Set([str(plan.format), ...FORMATS].filter(Boolean))].map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      </Field>
      <Field label="Shape">
        <select aria-label="Shape" className={selectCls} value={str(plan.mode) || 'table'} onChange={(e) => set('mode', e.target.value)}>
          {MODES.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </Field>
      <TextField label="Sheet / table (selector)" value={str(plan.selector)} onChange={(e) => set('selector', e.target.value)} spellCheck={false} placeholder="sheet:Sales, table:0, table:nth-of-type(2)" />
      <TextField label="Path to the records" value={str(plan.path)} onChange={(e) => set('path', e.target.value)} spellCheck={false} placeholder="data.items" />
      <TextField label="Time column" value={str(plan.timeCol)} onChange={(e) => set('timeCol', e.target.value)} spellCheck={false} />
      <Field label="Time format">
        <select aria-label="Time format" className={selectCls} value={str(plan.timeKind)} onChange={(e) => set('timeKind', e.target.value)}>
          <option value="">Date text</option>
          <option value="unix">Unix seconds</option>
          <option value="unixms">Unix milliseconds</option>
          <option value="year">Year</option>
        </select>
      </Field>
      <TextField label="Series column (splits into series)" value={str(plan.keyCol)} onChange={(e) => set('keyCol', e.target.value)} spellCheck={false} />
      <TextField label="Name column" value={str(plan.nameCol)} onChange={(e) => set('nameCol', e.target.value)} spellCheck={false} />
      <TextField label="Number columns (comma separated)" value={list(plan.numeric).join(', ')} onChange={(e) => set('numeric', csv(e.target.value))} spellCheck={false} />
      <TextField label="Text columns (comma separated)" value={list(plan.textCols).join(', ')} onChange={(e) => set('textCols', csv(e.target.value))} spellCheck={false} />
    </div>
  );
}

export function AskSourceCard({ mapping }: { mapping: Mapping }) {
  const { updateMapping } = useStore();
  const provider = str(mapping.spec?.provider);
  const [spec, setSpec] = useState<Obj>(() => structuredClone(obj(mapping.spec)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const dirty = JSON.stringify(spec) !== JSON.stringify(mapping.spec ?? {});
  const patch = (k: string, v: unknown) => {
    setSaved(false);
    setSpec((s) => ({ ...s, [k]: v }));
  };
  const plans: Obj[] = Array.isArray(spec.plans) ? (spec.plans as unknown[]).map(obj) : [];
  const setPlans = (next: Obj[]) => {
    setSaved(false);
    setSpec((s) => ({
      ...s,
      plans: next,
      // The Source's own target / format always follow its first document.
      ...(next[0] ? { target: str(next[0].url), format: str(obj(next[0].plan).format) || str(s.format) } : {}),
    }));
  };
  const setPlan = (i: number, p: Obj) => setPlans(plans.map((x, j) => (j === i ? p : x)));
  const interval = typeof spec.intervalSec === 'number' ? spec.intervalSec : 86400;
  const intervals = INTERVALS.some(([s]) => s === interval) ? INTERVALS : [...INTERVALS, [interval, `Every ${interval} s`] as [number, string]];

  const save = async () => {
    setError('');
    if (!str(spec.channel).trim()) return setError('Enter the channel this Source feeds.');
    if (!str(spec.template).trim()) return setError('Enter the schema this Source uses.');
    if (provider === 'web-auto') {
      if (plans.length === 0) return setError('Add at least one document.');
      if (plans.some((p) => !/^https:\/\//i.test(str(p.url).trim()))) return setError('Every document needs a full https:// address.');
    }
    setSaving(true);
    try {
      const next = { ...spec };
      if (provider === 'web-auto') {
        next.plans = plans.map((p) => ({ ...p, url: str(p.url).trim() }));
        next.target = str(plans[0].url).trim();
      }
      await updateMapping({ ...mapping, spec: next });
      setSpec(next);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppCard className="flex flex-col gap-4">
      <p className="m-0 text-xs text-black/55 dark:text-white/55">
        This Source was built by Ask Zmist. Everything it reads and writes is below; a change applies from the next poll.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <TextField label="Channel" value={str(spec.channel)} onChange={(e) => patch('channel', e.target.value)} spellCheck={false} />
        <TextField label="Schema" value={str(spec.template)} onChange={(e) => patch('template', e.target.value)} spellCheck={false} />
        <Field label="Read it">
          <select aria-label="Read it" className={selectCls} value={interval} onChange={(e) => patch('intervalSec', Number(e.target.value))}>
            {intervals.map(([s, l]) => (
              <option key={s} value={s}>{l}</option>
            ))}
          </select>
        </Field>
        {provider !== 'web-auto' && <TextField label="Address it polls" value={str(spec.target)} onChange={(e) => patch('target', e.target.value)} spellCheck={false} />}
        {provider === 'github-api' && (
          <TextField label="GitHub users / repositories (comma separated)" value={list(spec.targets).join(', ')} onChange={(e) => patch('targets', csv(e.target.value))} spellCheck={false} />
        )}
        {provider === 'sec-xbrl' && (
          <>
            <TextField label="Metric" value={str(spec.metric)} onChange={(e) => patch('metric', e.target.value)} spellCheck={false} />
            <TextField
              label="Company CIKs (comma separated)"
              value={list(spec.ciks).join(', ')}
              onChange={(e) => patch('ciks', csv(e.target.value).map((c) => (/^\d+$/.test(c) ? Number(c) : c)))}
              spellCheck={false}
            />
            <TextField label="Years back" type="number" value={String(spec.years ?? '')} onChange={(e) => patch('years', e.target.value === '' ? undefined : Number(e.target.value))} />
          </>
        )}
      </div>
      <Field label="Note">
        <textarea aria-label="Note" className={areaCls} value={str(spec.hint)} onChange={(e) => patch('hint', e.target.value)} />
      </Field>

      {provider === 'web-auto' && (
        <div className="flex flex-col gap-3">
          <span className="text-sm font-semibold">Documents</span>
          {plans.map((p, i) => (
            <div key={i} className="flex flex-col gap-3 rounded-xl border border-black/10 dark:border-white/10 p-3">
              <div className="flex items-end gap-2">
                <TextField
                  label={`Document ${i + 1} address (Google Sheets, Excel, Word, CSV, JSON, web page…)`}
                  value={str(p.url)}
                  onChange={(e) => setPlan(i, { ...p, url: e.target.value })}
                  spellCheck={false}
                />
                <Button variant="bordered" aria-label={`Remove document ${i + 1}`} disabled={plans.length === 1} onClick={() => setPlans(plans.filter((_, j) => j !== i))}>
                  <Trash2 size={15} />
                </Button>
              </div>
              <TextField label="Name prefix for its series" value={str(p.prefix)} onChange={(e) => setPlan(i, { ...p, prefix: e.target.value })} spellCheck={false} />
              <details>
                <summary className="text-xs text-black/55 dark:text-white/55 cursor-pointer select-none">How it is read (detected structure)</summary>
                <div className="mt-3">
                  <PlanFields plan={obj(p.plan)} onChange={(np) => setPlan(i, { ...p, plan: np })} />
                </div>
              </details>
            </div>
          ))}
          <Button variant="bordered" className="self-start" onClick={() => setPlans([...plans, { url: '', plan: { format: 'html', mode: 'table' } }])}>
            <Plus size={15} /> Add a document
          </Button>
        </div>
      )}

      {error && <span role="alert" className="text-xs text-red-500">{error}</span>}
      <div className="flex items-center gap-2">
        <Button variant="secondary" onClick={() => void save()} disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
        <Button variant="bordered" onClick={() => { setSpec(structuredClone(obj(mapping.spec))); setError(''); setSaved(false); }} disabled={saving || !dirty}>
          Discard
        </Button>
        {saved && !dirty && <span className="text-xs text-black/55 dark:text-white/55">Saved.</span>}
      </div>
    </AppCard>
  );
}
